import { useState, useRef, useEffect } from 'react';
import { IconX } from '@tabler/icons-react';

/**
 * Custom select dropdown with fixed-position portal.
 * Replaces native <select> to avoid mobile browser picker misalignment.
 *
 * Props:
 *   value        - currently selected value (string)
 *   onChange     - called with the new value string
 *   options      - [{ value, label }]
 *   className    - extra classes on the outer wrapper div
 *   triggerClassName - classes on the trigger button (defaults to "filter-field")
 *   triggerStyle - inline style for the trigger button
 *   placeholder  - label shown when no option matches (only if value === '')
 *   disabled     - when true, the trigger is greyed out and the menu won't open
 */
export default function SelectField({ value, onChange, options = [], className = '', triggerClassName, triggerStyle, placeholder, menuZIndex = 9999, disabled = false, clearable = false }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0, above: false });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const selectedLabel = options.find(o => String(o.value) === String(value))?.label
        ?? (placeholder && (value === '' || value === null || value === undefined) ? placeholder : options[0]?.label ?? '');

    // Position the fixed menu relative to its trigger. Recomputed on open and on
    // every scroll/resize while open, so the menu stays glued to its button.
    const computePos = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const menuH = Math.min(options.length * 40 + 8, 260);
        const spaceBelow = window.innerHeight - rect.bottom;
        const above = spaceBelow < menuH + 8;
        setPos({ top: above ? rect.top - 4 : rect.bottom + 4, left: rect.left, width: rect.width, above });
    };

    const openMenu = () => {
        if (disabled) return;
        computePos();
        setOpen(o => !o);
    };

    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        // `capture: true` so scrolls inside any ancestor container reposition the menu too.
        const reposition = () => computePos();
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close);
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('touchstart', close);
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open]);

    const defaultTriggerClass = triggerClassName ?? 'filter-field';
    const showClear = clearable && !disabled && value !== '' && value !== null && value !== undefined;

    return (
        <div style={{ position: 'relative', width: '100%' }} className={className}>
            <button
                ref={triggerRef}
                type="button"
                onClick={openMenu}
                disabled={disabled}
                className={`${defaultTriggerClass} w-full text-left flex items-center ${disabled ? 'opacity-50' : ''}`}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer', paddingRight: showClear ? 52 : 34, lineHeight: 1.5, ...triggerStyle }}
            >
                <span className="truncate flex-1 min-w-0" style={{ fontSize: 'inherit' }}>{selectedLabel}</span>
                <svg
                    style={{ position: 'absolute', right: 10, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, flexShrink: 0, opacity: 0.5, transition: 'transform 0.15s ease' }}
                    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {showClear && (
                <button
                    type="button"
                    aria-label="Clear"
                    onClick={(e) => { e.stopPropagation(); setOpen(false); onChange(''); }}
                    className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                    style={{ position: 'absolute', right: 30, top: '50%', transform: 'translateY(-50%)', padding: 2, borderRadius: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <IconX size={14} />
                </button>
            )}

            {open && (
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed',
                        top: pos.above ? 'auto' : pos.top,
                        bottom: pos.above ? window.innerHeight - pos.top : 'auto',
                        left: pos.left,
                        width: Math.max(pos.width, 160),
                        zIndex: menuZIndex,
                        maxHeight: 260,
                        overflowY: 'auto',
                    }}
                    className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-xl py-1"
                >
                    {options.map((opt) => {
                        const isSelected = String(opt.value) === String(value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => { onChange(opt.value); setOpen(false); }}
                                className={`w-full flex items-center px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${isSelected ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-zinc-700 dark:text-zinc-300'}`}
                            >
                                <span className="flex-1 min-w-0">{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
